import { getSelectedModel, setSelectedModel, MODEL_CHANGED_EVENT } from '@theme/model-preference';
import { ThemeEvents } from '@theme/events';

/**
 * Sitewide application of the shopper's remembered phone model (see
 * @theme/model-preference and @theme/model-picker). In the current
 * variant-based catalog every case fits every model, so this never changes
 * *which* products are shown — it only pre-selects the right variant so
 * "Add to cart" and the nav "Cases" link already match the shopper's phone.
 *
 * Every mutation below stashes the server-rendered value in a data attribute
 * before overwriting it, and reads the personalized value from that stash rather
 * than from the current DOM value. That does two things: clearing the preference
 * can restore the original in place (no page reload), and re-running apply() is
 * idempotent instead of re-parsing an already-personalized href.
 *
 * Known gap: cards rendered after this module runs by something other than a
 * filter update — the product-recommendations carousel, and the quick-add modal,
 * which carries its own `variantId` input — are not personalized. Their
 * "Add to cart" falls back to the product's default variant, which is correct,
 * just not pre-selected. Left as-is deliberately rather than observing the whole
 * document for mutations.
 */

/**
 * Reads the server-rendered value, stashing it on first touch.
 *
 * @param {HTMLElement} element
 * @param {string} key - dataset key holding the stash
 * @param {string} current - the value as the server rendered it
 * @returns {string}
 */
function originalValue(element, key, current) {
  if (element.dataset[key] == null) element.dataset[key] = current;
  return element.dataset[key];
}

/** @param {string | null} model */
function applyToNavLink(model) {
  const link = document.querySelector('[data-model-aware-link]');
  if (!(link instanceof HTMLAnchorElement)) return;

  const original = originalValue(link, 'modelDefaultHref', link.getAttribute('href') || '');

  if (!model) {
    link.setAttribute('href', original);
    return;
  }

  const url = new URL(original, window.location.origin);
  url.searchParams.set('model', model);
  link.href = `${url.pathname}${url.search}`;
}

/** @param {string | null} model */
function applyToProductCards(model) {
  for (const mapScript of document.querySelectorAll('[data-model-variant-map]')) {
    const card = mapScript.closest('product-card');
    if (!card) continue;

    // Every product link in the card, not just .product-card__link. A card also wraps
    // its gallery (and its title placeholder) in `a.contents` from card-gallery.liquid,
    // and clicking the image is the most natural thing a shopper does. Personalizing
    // only the first link left the image link pointing at the default model.
    const links = card.querySelectorAll('a[href*="/products/"]');
    const variantInput = card.querySelector('[ref="variantId"]');

    /** @type {Record<string, number | null>} */
    let map = {};
    try {
      map = JSON.parse(mapScript.textContent || '{}');
    } catch {
      // A malformed map only costs personalization on this card; the restore
      // path below still needs to run, so don't skip the card entirely.
    }

    const variantId = model ? map[model] : null;

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const original = originalValue(link, 'modelDefaultHref', link.getAttribute('href') || '');
      if (variantId) {
        const url = new URL(original, window.location.origin);
        url.searchParams.set('variant', String(variantId));
        link.href = `${url.pathname}${url.search}`;
      } else {
        link.setAttribute('href', original);
      }
    }

    if (variantInput instanceof HTMLInputElement) {
      const original = originalValue(variantInput, 'modelDefaultValue', variantInput.value);
      variantInput.value = variantId ? String(variantId) : original;
    }
  }
}

/**
 * @param {string | null} model
 * @param {boolean} respectDeepLink - true on page load, false when the shopper
 *   just changed the model themselves.
 */
function applyToProductPage(model, respectDeepLink) {
  if (!model) return;
  // On page load an explicit ?variant= means someone deep-linked to a specific
  // variant on purpose (a share link, an ad), so don't override it. That does NOT
  // apply once the shopper picks a model in the header: by then `?variant=` is
  // usually just the variant this page already switched to, and refusing to move
  // would leave the header and the PDP disagreeing.
  if (respectDeepLink && new URLSearchParams(window.location.search).has('variant')) return;

  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!picker) return;

  // Buttons / swatches style: the option values are radio inputs.
  const radio = picker.querySelector(`input[type="radio"][value="${CSS.escape(model)}"]`);
  if (radio instanceof HTMLInputElement) {
    if (!radio.checked) radio.click();
    return;
  }

  // Dropdowns style (what product.json currently uses): the option values are
  // <option>s. variant-picker.js listens for a bubbling `change` on itself, so
  // dispatching one drives the same path a real selection would.
  for (const select of picker.querySelectorAll('select.variant-option__select')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    const match = [...select.options].some((option) => option.value === model);
    if (!match || select.value === model) continue;

    select.value = model;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }
}

/**
 * Reads the Model option currently selected in the PDP's own variant picker.
 * Matches on the option NAME rather than a block id, so it survives the picker
 * being re-rendered or the block being reconfigured.
 *
 * @returns {string | null}
 */
function readProductPageModel() {
  const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!picker) return null;

  // Dropdowns style: <select name="options[Model]">
  for (const select of picker.querySelectorAll('select[name]')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    if (/^options\[model\]$/i.test(select.name)) return select.value || null;
  }

  // Buttons / swatches style: <input type="radio" name="Model-<blockId>-<productId>">
  for (const radio of picker.querySelectorAll('input[type="radio"]:checked')) {
    if (!(radio instanceof HTMLInputElement)) continue;
    if (/^model-/i.test(radio.name)) return radio.value || null;
  }

  return null;
}

/**
 * The reverse direction: a shopper who picks their phone in the PDP's own variant
 * picker has just told us their model, so remember it. Without this the header
 * picker keeps advertising the previous phone and every later product-card link
 * points at the wrong variant.
 *
 * Safe against feedback loops: setSelectedModel fires MODEL_CHANGED_EVENT, which
 * re-runs apply(), but applyToProductPage bails when the select already holds that
 * value (and again on the `?variant=` guard), so nothing bounces back.
 */
function rememberProductPageModel() {
  const model = readProductPageModel();
  if (!model || model === getSelectedModel()) return;
  setSelectedModel(model);
}

// Never revert the PDP selection when the preference is cleared. Once a shopper
// is looking at a variant, silently switching it back under them is worse than
// leaving it where they can see it.
/** @param {boolean} [respectDeepLink] */
function apply(respectDeepLink = true) {
  const model = getSelectedModel();
  applyToNavLink(model);
  applyToProductCards(model);
  applyToProductPage(model, respectDeepLink);
}

// Module scripts run after the document is parsed, so the DOM is already
// ready by the time this executes (no DOMContentLoaded wait needed).
apply();

// Landing on a PDP deep-linked to a specific variant (a shared link, an ad, or one
// of our own personalized card links): the PDP keeps that variant, so adopt its
// model as the remembered one, otherwise the header would sit there advertising a
// different phone than the page the shopper is looking at.
//
// Gated on an explicit `?variant=` on purpose. Without it a PDP just shows the
// product's default variant, and adopting that would tell shoppers their phone is
// whatever the first variant happens to be, which they never chose.
if (new URLSearchParams(window.location.search).has('variant')) {
  rememberProductPageModel();
}

// A model change is an explicit choice by the shopper, so it overrides the
// deep-link guard: the PDP follows the header rather than staying put.
document.addEventListener(MODEL_CHANGED_EVENT, () => apply(false));

// Filtering and pagination swap the results list for freshly rendered cards,
// which arrive with the server's default hrefs and no stash.
document.addEventListener(ThemeEvents.FilterUpdate, apply);

// Picking a model in the PDP's own variant picker updates the remembered model,
// which keeps the header picker and every product-card link in agreement with it.
document.addEventListener(ThemeEvents.variantUpdate, rememberProductPageModel);

// Back/forward out of the bfcache restores the old DOM without re-running the
// module, so re-apply in case the preference changed on another page.
window.addEventListener('pageshow', apply);
